"use client";

import {proxy} from "valtio";

import type {OurPropertySectionData} from "@/features/flexible/our-property/type";

import type {Rental, RentalCardProps} from "@/entities/rental";
import {fetchPageUnits as fetchPageUnitsDefault, searchUnits as searchUnitsDefault} from "@/entities/rental/api/actions";
import {buildSearchRequest} from "@/entities/rental/api/helpers/search-request.builder";
import type {VacasaSearchRequest} from "@/entities/rental/api/types/search.types";

import {capitalizeFirstLetter} from "@/shared/lib";
import {DateHelper} from "@/shared/lib/helpers/date";
import {IValtioModel} from "@/shared/models/base-model.interface";
import {QueryState, type QueryStateInstance} from "@/shared/services";
import {AnalyticsService, type AnalyticsServiceInstance, type OurPropertySectionType} from "@/shared/services/analytics/analytics.service";
import type {TagBaseProps} from "@/shared/ui/data-display/tags/tag-base";
import {routes} from "@/shared/utils/routing";

interface TagData {
    id: string;
    label: string;
    icon?: string;
    urlType: "search" | "singlePage";
    url?: string; // For search type
    urls?: Array<string>; // For singlePage type
}

export interface PropertyListModelConfig {
    sectionData: OurPropertySectionData;
    initialPlaces: Array<Rental>;
    initialTotal: number;
    initialPages: Array<Array<string>>;
    initialSearchUrl?: string | null;
    initialViewAllUrl?: string | null;
    showCount?: number;
    searchUnits: typeof searchUnitsDefault;
    fetchPageUnits: typeof fetchPageUnitsDefault;
}

type SearchQueryResult = {places: Array<Rental>; total: number; pages?: Array<Array<string>>};
type PaginationQueryResult = {places: Array<Rental>};

class PropertyListModelClass implements IValtioModel {
    readonly sectionData: OurPropertySectionData;
    places: Array<Rental> = [];
    total: number = 0;
    pages: Array<Array<string>> = []; // Pagination cache for search mode
    currentPage: number = 1;
    visibleCount: number = 8;

    // Store active search URL and filters for pagination
    private activeSearchUrl: string | null = null;
    private searchFilters: VacasaSearchRequest = {};
    private pendingFilterUrl: string | null = null;
    private readonly initialViewAllUrl: string;

    // Tags from propertyItems (for search mode)
    private readonly tags: Array<TagData> = [];
    activeTagId: string | null = null;

    readonly defaultItemsPerPage: number;
    itemsPerPage: number;
    private readonly pageSize: number = 24;

    // Query states
    readonly searchQuery: QueryStateInstance<SearchQueryResult>;
    readonly paginationQuery: QueryStateInstance<PaginationQueryResult>;
    readonly shuffleQuery: QueryStateInstance<SearchQueryResult>;
    readonly filterQuery: QueryStateInstance<SearchQueryResult>;

    private readonly searchUnits: typeof searchUnitsDefault;
    private readonly fetchPageUnits: typeof fetchPageUnitsDefault;
    private readonly analytics: AnalyticsServiceInstance;

    constructor(config: PropertyListModelConfig) {
        this.searchUnits = config.searchUnits;
        this.fetchPageUnits = config.fetchPageUnits;
        this.analytics = AnalyticsService();
        this.sectionData = config.sectionData;
        this.places = config.initialPlaces;
        this.total = config.initialTotal;
        this.pages = config.initialPages;
        this.defaultItemsPerPage = config.showCount ?? config.sectionData.itemsCount ?? 8;
        this.itemsPerPage = this.defaultItemsPerPage;
        this.visibleCount = this.itemsPerPage;

        // Store initial search URL from controller
        this.activeSearchUrl = config.initialSearchUrl || null;
        this.initialViewAllUrl = config.initialViewAllUrl || "";

        // Build tags for search mode
        if (this.sectionData.sectionType === "search" && this.sectionData.propertyItems) {
            this.tags = this.buildTags();
            // First tag is active by default
            if (this.tags.length > 0) {
                this.activeTagId = this.tags[0].id;
            }
        }

        // Set currentPage to 0 (first page already loaded)
        this.currentPage = 0;

        // Initialize search query (for tag switching)
        this.searchQuery = QueryState<SearchQueryResult>(async (_signal: AbortSignal) => {
            const result = await this.loadActiveTagData();
            this.onSearchSuccess(result);
            return result;
        });

        // Initialize pagination query (for loading more pages)
        this.paginationQuery = QueryState<PaginationQueryResult>(async (_signal: AbortSignal) => {
            const result = await this.loadNextPage();
            this.onPaginationSuccess(result);
            return result;
        });

        // Initialize shuffle query
        this.shuffleQuery = QueryState<SearchQueryResult>(async (_signal: AbortSignal) => {
            const result = await this.performShuffle();
            this.onShuffleSuccess(result);
            return result;
        });

        // Initialize filter query (for dates/guests filtering, in place)
        this.filterQuery = QueryState<SearchQueryResult>(async (_signal: AbortSignal) => {
            const result = await this.loadFromSearchUrl(this.pendingFilterUrl ?? this.activeSearchUrl ?? "");
            this.onSearchSuccess(result);
            return result;
        });
    }

    // Build tags from propertyItems
    private buildTags(): Array<TagData> {
        return (this.sectionData.propertyItems || []).map((item, index) => ({
            id: `tag-${index}`,
            label: item.label || "Untitled",
            icon: item.icon?.url, // Use UnifiedImage.url directly
            urlType: item.urlType,
            url: item.url,
            urls: item.singlePageUrls,
        }));
    }

    // Get active tag
    private getActiveTag(): TagData | null {
        return this.tags.find((tag) => tag.id === this.activeTagId) || null;
    }

    /**
     * Get search filters from active search URL
     */
    private getSearchFilters(): VacasaSearchRequest {
        if (!this.activeSearchUrl) {
            return this.searchFilters;
        }

        try {
            const urlObj = new URL(this.activeSearchUrl);
            const urlSearchParams = new URLSearchParams(urlObj.search);
            const filters = routes.search.parse(urlSearchParams);

            // Convert to Vacasa API format
            return buildSearchRequest(filters);
        } catch (_error) {
            return this.searchFilters;
        }
    }

    /**
     * Fetch page with all search filters applied
     */
    private async fetchPage(pageIndex: number, unitIds: Array<string>): Promise<{places: Array<Rental>}> {
        const filters = this.getSearchFilters();
        return this.fetchPageUnits({
            pageNumber: pageIndex,
            totalUnits: this.total,
            unitIds,
            ...filters,
        });
    }

    get sectionType(): OurPropertySectionType {
        return this.sectionData.sectionType === "suggestions" ? "suggestions" : "search";
    }

    // Tags with handlers for UI
    get tagsWithHandlers(): Array<Omit<TagBaseProps, "onClick">> {
        return this.tags.map((tag) => ({
            label: capitalizeFirstLetter(tag.label),
            icon: tag.icon,
            value: tag.id,
            active: this.activeTagId === tag.id,
        }));
    }

    get viewAllUrl(): string {
        if (!this.activeSearchUrl) {
            return this.initialViewAllUrl;
        }

        try {
            const urlObj = new URL(this.activeSearchUrl);
            const urlSearchParams = new URLSearchParams(urlObj.search);
            const filters = routes.search.parse(urlSearchParams);

            return routes.search.url(filters);
        } catch (_error) {
            return this.initialViewAllUrl;
        }
    }

    // Convert places to RentalCardProps
    get properties(): Array<RentalCardProps> {
        return this.places.map((rental) => ({
            id: rental.id,
            title: rental.title,
            location: rental.location,
            image: rental.image,
            rating: rental.rating,
            persons: rental.persons,
            beds: rental.beds,
            bathrooms: rental.bathrooms,
            pricePerNight: rental.pricePerNight,
            itinerary: rental.itinerary,
            link: rental.slug ? routes.rental.detail.url(rental.slug) : undefined,
        }));
    }

    // Visible properties (limited by visibleCount)
    get visibleProperties(): Array<RentalCardProps> {
        return this.properties.slice(0, this.visibleCount);
    }

    // Check if has more items to show
    get hasMoreItems(): boolean {
        return this.visibleCount < this.places.length || this.hasMorePages;
    }

    // Check if has more pages to load
    get hasMorePages(): boolean {
        // If we have pages cache, use its length (currentPage is 0-indexed)
        if (this.pages.length > 0) {
            return this.currentPage + 1 < this.pages.length;
        }

        // Fallback to calculation (shouldn't happen normally)
        const totalPages = Math.ceil(this.total / this.pageSize);
        return this.currentPage + 1 < totalPages;
    }

    // Check if show button should be displayed
    get showButton(): boolean {
        return this.hasMoreItems;
    }

    // Check if read less button should be displayed (only when all pages are loaded and all items are shown)
    get showReadLessButton(): boolean {
        // Show only when:
        // 1. All pages are loaded (!hasMorePages)
        // 2. All loaded items are shown (visibleCount >= places.length)
        // 3. More than initial count is shown (visibleCount > itemsPerPage)
        // 4. No more items to show (!hasMoreItems)
        return !this.hasMorePages && this.visibleCount >= this.places.length && this.visibleCount > this.itemsPerPage && !this.hasMoreItems;
    }

    // Check if shuffle button should be displayed (only for suggestions)
    get showShuffleButton(): boolean {
        return this.total > this.visibleCount;
    }

    // Check if loading
    get isLoading(): boolean {
        return this.searchQuery.isPending || this.paginationQuery.isPending;
    }

    // Check if filtering (searching/switching tags, shuffling, or applying dates/guests filter)
    get isFiltering(): boolean {
        return this.searchQuery.isPending || this.shuffleQuery.isPending || this.filterQuery.isPending;
    }

    // Check if pagination loading
    get isPaginationLoading(): boolean {
        return this.paginationQuery.isPending;
    }

    /**
     * Load data for active tag
     */
    private async loadActiveTagData(): Promise<SearchQueryResult> {
        const activeTag = this.getActiveTag();
        if (!activeTag) {
            return {places: [], total: 0};
        }

        // Search URL
        if (activeTag.urlType === "search" && activeTag.url) {
            return this.loadFromSearchUrl(activeTag.url);
        }

        // Single page URLs
        if (activeTag.urlType === "singlePage" && activeTag.urls && activeTag.urls.length > 0) {
            return this.loadFromSinglePageUrls(activeTag.urls);
        }

        return {places: [], total: 0};
    }

    /**
     * Load from search URL
     */
    private async loadFromSearchUrl(url: string): Promise<SearchQueryResult> {
        try {
            const urlObj = new URL(url);
            const urlSearchParams = new URLSearchParams(urlObj.search);
            const filters = routes.search.parse(urlSearchParams);

            // Store URL and filters for pagination
            this.activeSearchUrl = url;
            this.searchFilters = buildSearchRequest(filters);

            const result = await this.searchUnits(filters);

            // Use original pages from API (pages[0] = 24 IDs, pages[1] = 5 IDs, etc.)
            return {
                places: result.places,
                total: result.pagination.total,
                pages: result.pages || [], // Use original pages from API
            };
        } catch (error) {
            console.error("Failed to load from search URL:", error);
            return {places: [], total: 0};
        }
    }

    /**
     * Load from single page URLs
     */
    private async loadFromSinglePageUrls(urls: Array<string>): Promise<SearchQueryResult> {
        try {
            const propertyIds = routes.rental.detail.extractIds(urls);

            if (propertyIds.length === 0) {
                return {places: [], total: 0};
            }

            // Clear search URL and filters as we're using single page mode
            this.activeSearchUrl = null;
            this.searchFilters = {};

            // Create pages array - split IDs into batches of itemsPerPage
            const pages: Array<Array<string>> = [];
            for (let i = 0; i < propertyIds.length; i += this.itemsPerPage) {
                pages.push(propertyIds.slice(i, i + this.itemsPerPage));
            }

            // Load first batch (itemsPerPage)
            const firstBatch = pages[0] || [];
            const result = await this.fetchPage(0, firstBatch);

            return {
                places: result.places,
                total: propertyIds.length,
                pages: pages,
            };
        } catch (error) {
            console.error("Failed to load from single page URLs:", error);
            return {places: [], total: 0};
        }
    }

    /**
     * Load next page
     */
    private async loadNextPage(): Promise<PaginationQueryResult> {
        // currentPage is already incremented before this call
        const pageIndex = this.currentPage;

        // Check if we have pages cache
        if (this.pages.length === 0 || pageIndex >= this.pages.length) {
            return {places: []};
        }

        try {
            const unitIdsForPage = this.pages[pageIndex];
            const result = await this.fetchPage(pageIndex, unitIdsForPage);
            return {places: result.places};
        } catch (error) {
            console.error("Failed to load next page:", error);
            return {places: []};
        }
    }

    /**
     * Handle search success (new tag data)
     */
    private onSearchSuccess(result: SearchQueryResult): void {
        this.places = result.places;
        this.total = result.total;
        this.pages = result.pages || [];
        this.currentPage = 0; // Reset to 0 (first page loaded)
        this.visibleCount = this.itemsPerPage;
    }

    /**
     * Handle pagination success (append data)
     */
    private onPaginationSuccess(result: PaginationQueryResult): void {
        this.places = [...this.places, ...result.places];
    }

    /**
     * Handle shuffle success (replace data)
     */
    private onShuffleSuccess(result: SearchQueryResult): void {
        this.places = result.places;
        this.visibleCount = this.itemsPerPage;
    }

    /**
     * Set items per page (for responsive breakpoints)
     */
    setItemsPerPage(count: number): void {
        const wasAtInitial = this.visibleCount === this.itemsPerPage;
        this.itemsPerPage = count;
        if (wasAtInitial) {
            this.visibleCount = count;
        }
    }

    /**
     * Toggle tag (switch to different tag)
     */
    toggleTag(tagId: string): void {
        if (this.activeTagId === tagId) {
            return;
        }

        this.activeTagId = tagId;
        void this.searchQuery.execute();

        const tag = this.tags.find((t) => t.id === tagId);
        if (tag) {
            this.analytics.trackPropertyTagClick({tag: tag.label, section_type: this.sectionType});
        }
    }

    /**
     * Apply a dates/guests filter to the current search URL and reload the list in place
     * (no navigation - keeps location/tag scope untouched).
     */
    async applyDatesGuestsFilter(filters: {
        startDate: Date | null;
        endDate: Date | null;
        adults: number;
        children: number;
        pets: number;
    }): Promise<void> {
        if (!this.activeSearchUrl) {
            return;
        }

        const urlObj = new URL(this.activeSearchUrl);
        const {startDate, endDate, adults, children, pets} = filters;

        if (startDate) {
            urlObj.searchParams.set("checkIn", DateHelper.formatDateForUrl(startDate));
        } else {
            urlObj.searchParams.delete("checkIn");
        }

        if (endDate) {
            urlObj.searchParams.set("checkOut", DateHelper.formatDateForUrl(endDate));
        } else {
            urlObj.searchParams.delete("checkOut");
        }

        if (adults > 0) {
            urlObj.searchParams.set("adults", String(adults));
        } else {
            urlObj.searchParams.delete("adults");
        }

        if (children > 0) {
            urlObj.searchParams.set("children", String(children));
        } else {
            urlObj.searchParams.delete("children");
        }

        if (pets > 0) {
            urlObj.searchParams.set("pets", String(pets));
        } else {
            urlObj.searchParams.delete("pets");
        }

        this.pendingFilterUrl = urlObj.toString();
        await this.filterQuery.execute();
    }

    /**
     * Load more - show more from loaded or load next page
     */
    async loadMore(): Promise<void> {
        this.analytics.trackPropertyLoadMore({section_type: this.sectionType});

        const newVisibleCount = this.visibleCount + this.itemsPerPage;

        // If we have enough loaded places locally, just show more
        if (newVisibleCount <= this.places.length) {
            this.visibleCount = newVisibleCount;
            return;
        }

        // Show all currently loaded places
        if (this.visibleCount < this.places.length) {
            this.visibleCount = this.places.length;
        }

        // If we need more and have more pages, load next page from API
        if (this.hasMorePages && this.visibleCount >= this.places.length) {
            const previousCount = this.visibleCount;
            this.currentPage++;
            await this.paginationQuery.execute();
            // After loading, show only itemsPerPage more items (not all loaded)
            this.visibleCount = Math.min(previousCount + this.itemsPerPage, this.places.length);
        }
    }

    /**
     * Show less - reset to initial count
     * Note: Scroll to section should be handled in component using ref
     */
    showLess(): void {
        this.visibleCount = this.itemsPerPage;
    }

    /**
     * Shuffle - reshuffle pages and reload first batch
     */
    shuffle(): void {
        void this.shuffleQuery.execute();
        this.analytics.trackPropertyShuffle({section_type: this.sectionType});
    }

    /**
     * Perform shuffle operation
     */
    private async performShuffle(): Promise<SearchQueryResult> {
        if (this.pages.length === 0) {
            // Fallback: shuffle current places if no pages cache
            const shuffledPlaces = this.shuffleArray([...this.places]);
            return {
                places: shuffledPlaces,
                total: this.total,
                pages: [],
            };
        }

        // Flatten all pages into single array of IDs
        const allIds: Array<string> = [];
        this.pages.forEach((page) => {
            allIds.push(...page);
        });

        // Shuffle all IDs
        const shuffledIds = this.shuffleArray(allIds);

        // Recreate pages from shuffled IDs
        const newPages: Array<Array<string>> = [];
        for (let i = 0; i < shuffledIds.length; i += this.itemsPerPage) {
            newPages.push(shuffledIds.slice(i, i + this.itemsPerPage));
        }

        // Update pages cache
        this.pages = newPages;
        this.currentPage = 0; // Reset to 0 (first page loaded)

        // Reload first batch from shuffled pages
        try {
            const result = await this.fetchPage(0, this.pages[0]);

            return {
                places: result.places,
                total: this.total,
                pages: newPages,
            };
        } catch (error) {
            console.error("Failed to shuffle:", error);
            return {places: [], total: 0, pages: []};
        }
    }

    /**
     * Shuffle array helper (Fisher-Yates algorithm)
     */
    private shuffleArray<T>(array: Array<T>): Array<T> {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    }

    init(): void {
        // Data already loaded on server
    }
}

export type PropertyListModelInstance = PropertyListModelClass;

export {PropertyListModelClass};

type PublicPropertyListModelConfig = Omit<PropertyListModelConfig, "searchUnits" | "fetchPageUnits">;

export function PropertyListModel(config: PublicPropertyListModelConfig): PropertyListModelInstance {
    return proxy(new PropertyListModelClass({...config, searchUnits: searchUnitsDefault, fetchPageUnits: fetchPageUnitsDefault}));
}
